import { SUPABASE_URL, SUPABASE_ANON_KEY } from '../config/env';

function browserOpenAIBlocked() {
  return Promise.reject(new Error('OpenAI is server-only. The staff web app no longer ships an API key.'));
}

const openai = {
  chat: { completions: { create: browserOpenAIBlocked } },
  audio: { transcriptions: { create: browserOpenAIBlocked } },
};

/**
 * Transliterate Devanagari to Roman script using GPT (FAST version)
 * @param {string} text - Text with mixed Devanagari and English
 * @returns {Promise<string>} - Romanized text
 */
async function transliterateToRoman(text) {
  try {
    // Check if text contains Devanagari characters
    const hasDevanagari = /[\u0900-\u097F]/.test(text);
    
    if (!hasDevanagari) return text; // Already in Roman script - FAST PATH
    
    const response = await openai.chat.completions.create({
      model: "gpt-4o-mini",
      messages: [{
        role: "user",
        content: `Convert to Hinglish (Roman script only). Keep it natural.

Examples: "हाँ जी" → "haan ji", "गाड़ी" → "gaadi", "ठीक है" → "thik hai"

Text: ${text}

Output ONLY converted text:`
      }],
      temperature: 0.2,
      max_tokens: 500, // Limit for faster response
    });
    
    const result = response.choices[0].message.content.trim();
    return result;
  } catch (error) {
    console.error('❌ Transliteration error:', error);
    return text; // Return original if transliteration fails
  }
}

/**
 * Clean transcript (remove repetition/noise, light Hinglish grammar fixes) WITHOUT changing meaning.
 * Uses GPT-4o-mini as per the provided "whisper prompt" instructions.
 */
async function cleanTranscript(text) {
  try {
    const input = (text || '').trim();
    if (!input) return '';

    const prompt = `Clean this transcription:
- Remove repeated words
- Fix Hinglish grammar but keep meaning same
- Keep emotions or tone intact
- Do NOT change facts
Clean this transcript without changing meaning.
Fix Hindi/Hinglish grammar lightly. Remove noise, repetition, fillers.
Do not change facts.

Transcript:
${input}

Output ONLY the cleaned transcript text.`;

    const response = await openai.chat.completions.create({
      model: "gpt-4o-mini",
      messages: [{ role: "user", content: prompt }],
      temperature: 0.2,
      max_tokens: 1200,
    });

    return (response.choices?.[0]?.message?.content || '').trim() || input;
  } catch (error) {
    console.warn('⚠️ cleanTranscript failed, using raw transcript:', error?.message || error);
    return (text || '').trim();
  }
}

export const transcriptionService = {
  async markTranscriptionStatus(callId, status, extra = {}) {
    try {
      const url = `${SUPABASE_URL}/rest/v1/call_reports?callid=eq.${callId}`;
      const payload = {
        transcription_status: status,
        // Clear old error whenever we start processing again / complete successfully
        ...(status === 'processing' || status === 'completed' ? { transcription_error: null } : {}),
        ...extra,
      };

      const response = await fetch(url, {
        method: 'PATCH',
        headers: {
          'apikey': SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
          'Content-Type': 'application/json',
          'Prefer': 'return=representation',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText);
      }

      const data = await response.json();
      return { success: true, data };
    } catch (error) {
      console.warn('⚠️ markTranscriptionStatus failed:', error?.message || error);
      return { success: false, error: error?.message || String(error) };
    }
  },

  /**
   * Transcribe audio file using OpenAI Whisper API
   * @param {string} audioUrl - URL of the audio file
   * @returns {Promise<{success: boolean, transcription?: string, error?: string}>}
   */
  async transcribeAudio(audioUrl) {
    try {
      // Fetch audio file
      const audioResponse = await fetch(audioUrl, { mode: 'cors' });
      if (!audioResponse.ok) {
        throw new Error(`Failed to fetch audio: ${audioResponse.status}`);
      }
      
      const audioBlob = await audioResponse.blob();

      // Cost-safety: skip extremely small/likely-invalid recordings without calling OpenAI
      // (these often produce "Hello Hello..." or single-word loops)
      if (audioBlob.size < 20_000) {
        throw new Error('Recording too short/empty for reliable transcription');
      }
      
      // Create File object for OpenAI API
      const audioFile = new File([audioBlob], 'recording.mp3', { type: 'audio/mpeg' });
      
      // Use Whisper with SMART prompt for better Hindi/Hinglish accuracy
      
      // BEST PRACTICE: Short example sentence with expected vocabulary
      // This helps Whisper understand the context and vocabulary style
      const whisperPrompt = "Hello sir namaste, gaadi ka problem kya hai? Battery kharab ho gaya hai. Mechanic ko location bhej dena. Thik hai ji, dhanyawad.";
      
      const transcription = await openai.audio.transcriptions.create({
        file: audioFile,
        model: "whisper-1",
        response_format: "text",
        temperature: 0, // More accurate
        prompt: whisperPrompt, // Context hint with Hinglish vocabulary
      });
      
      // Smart garbage detection - only reject truly broken transcriptions
      const hasRepeatedChars = /(.)\1{15,}/.test(transcription); // 15+ same chars
      const tooShort = transcription.length < 20;
      
      // Check if ENTIRE transcription is just repetition (not just phrases)
      const words = transcription.split(/\s+/);
      const totalWords = words.length;
      
      // If transcription is MOSTLY the same 3-word phrase repeated
      if (totalWords >= 10) {
        const threeWordPhrases = [];
        for (let i = 0; i <= words.length - 3; i++) {
          threeWordPhrases.push(words.slice(i, i + 3).join(' '));
        }
        const phraseCount = {};
        threeWordPhrases.forEach(phrase => {
          phraseCount[phrase] = (phraseCount[phrase] || 0) + 1;
        });
        const maxRepeat = Math.max(...Object.values(phraseCount));
        const repeatPercentage = (maxRepeat / threeWordPhrases.length) * 100;
        
        // Only flag as garbage if >70% is the same phrase
        const isReallyStuck = repeatPercentage > 70 && maxRepeat >= 10;
        
        if (isReallyStuck) {
          console.warn('⚠️ True garbage detected - extreme repetition!');
          console.warn('  - Max repeat:', maxRepeat, 'Percentage:', repeatPercentage.toFixed(1) + '%');
          throw new Error('Transcription stuck in loop - audio quality is very poor');
        }
      }
      
      if (hasRepeatedChars || tooShort) {
        console.warn('⚠️ Invalid transcription detected!');
        console.warn('  - Repeated chars:', hasRepeatedChars);
        console.warn('  - Too short:', tooShort);
        throw new Error('Transcription quality check failed - audio may be unclear');
      }
      
      // Post-process: Convert any Devanagari to Roman script
      const romanizedTranscription = await transliterateToRoman(transcription);

      // Second pass cleaning (remove repetition/noise, light grammar fixes, keep facts)
      const cleaned = await cleanTranscript(romanizedTranscription);
      
      return { success: true, transcription: cleaned || romanizedTranscription };
    } catch (error) {
      console.error('❌ Transcription error:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Generate business-focused summary from transcription text using GPT-4o-mini
   * @param {string} transcriptionText - The call transcription text
   * @returns {Promise<{success: boolean, summary?: string, error?: string}>}
   */
  async generateSummary(transcriptionText) {
    try {
      const prompt = `You are an AI Call Assistant for a Roadside Assistance company.
Your job is to deeply understand the call transcript and produce a business-focused,
highly accurate summary with insights. DO NOT assume anything that is not clearly said.
Handle Hindi, Hinglish, English, broken speech, noise, slang.

Rules
- If something is unknown say: "NOT CLEAR"
- Never create fake info
- Extract only useful operational details
- Keep tone factual, not emotional
- Understand emergency vs normal breakdown
- Understand accident vs mechanical vs battery vs tyre vs towing
- Handle city + area + landmark properly
- Vehicle details detect only if clearly spoken
- If agent made mistake, capture it

OUTPUT STRICTLY IN THIS FORMAT:

Customer Issue:
<1–2 line real problem summary>

Resolution:
<What the team did / promised / next step>

Vehicle:
<Car name / type / unknown>

Service Required:
<towing / jump start / mechanic / battery / tyre / accident support / unknown>

Sentiment:
<angry / frustrated / confused / calm / neutral / happy>

Urgency Level:
<LOW / MEDIUM / HIGH>

Key Points:
1. ...
2. ...
3. ...
4. ...
5. ...

Action Items:
- ...

Location:
<city + area + landmark if available>

Follow-up Required:
YES / NO

Quality Check:
Agent Handling: GOOD / AVERAGE / POOR
Issue in Call Handling: <if any>
Suggested Improvement: <if any>

Important:
- Hindi me call ho to bhi samajh kar clean structured output do
- Explain only what is actually said
- Be consistent

Transcript:
${transcriptionText}`;

      const chatCompletion = await openai.chat.completions.create({
        model: "gpt-4o-mini",
        messages: [{ role: "user", content: prompt }],
        temperature: 0.2,
        max_tokens: 900,
      });

      const summaryText = (chatCompletion.choices?.[0]?.message?.content || '').trim();
      return { success: true, summary: summaryText };
    } catch (error) {
      console.error("Error generating summary:", error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Save transcription and summary to Supabase
   * @param {string} callId - The call ID
   * @param {string} transcription - The transcription text
   * @param {object} summary - The AI-generated summary object
   * @param {string} status - Transcription status (default: 'completed')
   * @returns {Promise<{success: boolean, data?: any, error?: string}>}
   */
  async saveTranscription(callId, transcription, summary, status = 'completed') {
    try {
      const url = `${SUPABASE_URL}/rest/v1/call_reports?callid=eq.${callId}`;
      
      const payload = {
        transcription: transcription,
        // Store summary as TEXT (new format). If legacy object is passed, stringify safely.
        summary: typeof summary === 'string' ? summary : JSON.stringify(summary),
        transcription_status: status,
        transcription_error: null,
        transcribed_at: new Date().toISOString(),
      };
      
      const response = await fetch(url, {
        method: 'PATCH',
        headers: {
          'apikey': SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
          'Content-Type': 'application/json',
          'Prefer': 'return=representation',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to save transcription: ${errorText}`);
      }

      const data = await response.json();
      
      if (!data || data.length === 0) {
        return { success: false, error: 'Failed to save transcription (empty response).' };
      }
      
      return { success: true, data: data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },
};
